import { useEffect, useState } from 'react';
import { Button, ButtonProps, Stack, StackItem } from '@optimus-web/core';
import { OverlayedLoader } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { Optional } from '@mews/std';
import { ResourceInfo, ResourceInfoProps } from './resourceInfo';
import {
    FormChecks,
    FormChecksProps,
    FormContent,
    useDailySellLimitsFormControls,
    UseDailySellLimitsFormControlsParams,
} from './form';
import { MIN_DAILY_SELL_LIMIT } from './constants';

const contentWidth = '345px';

type PopoverContentProps = Omit<UseDailySellLimitsFormControlsParams, 'defaultValues' | 'resourceCategoryIds'> &
    Omit<ResourceInfoProps, 'isFormVisible'> &
    Omit<FormChecksProps, 'hasAvailableResources' | 'children'> & {
        absolutePerTimeUnitLimit: Optional<number>;
        closePopover: () => void;
        resourceCategoryId: Optional<string>;
    };

export const PopoverContent = ({
    absolutePerTimeUnitLimit,
    availableResources,
    closePopover,
    enterpriseId,
    hasAllResourcesLimitReached,
    hasRateGroupLimitReached,
    isAllResourcesCategory,
    occupancy,
    rateGroupIds,
    rateIds,
    resourceCategoryId,
    serviceId,
    sold,
    timeUnit,
}: PopoverContentProps) => {
    const hasDailyLimit = !!absolutePerTimeUnitLimit;
    const [isFormVisible, setFormVisibility] = useState(hasDailyLimit);

    useEffect(() => {
        setFormVisibility(hasDailyLimit);
    }, [hasDailyLimit, timeUnit, resourceCategoryId]);

    const initialValue = sold != null ? sold + 1 : MIN_DAILY_SELL_LIMIT;
    const defaultValues = { absolutePerTimeUnitLimit: absolutePerTimeUnitLimit || initialValue };
    const resourceCategoryIds = resourceCategoryId != null ? [resourceCategoryId] : resourceCategoryId;

    const { submit, hasDirtyFields, isSubmitting, removeLimits, renderFormContent } = useDailySellLimitsFormControls({
        defaultValues,
        timeUnit,
        onRemove: closePopover,
        rateGroupIds,
        rateIds,
        resourceCategoryIds,
        serviceId,
    });

    const handleShowFormClick: ButtonProps['onClick'] = (e) => {
        e.nativeEvent.stopImmediatePropagation();
        setFormVisibility(true);
    };

    return renderFormContent(
        <StackItem width={contentWidth} grow>
            <Stack spacing="200" vertical>
                <ResourceInfo
                    availableResources={availableResources}
                    occupancy={occupancy}
                    sold={sold}
                    isFormVisible={isFormVisible}
                />
                <FormChecks
                    isAllResourcesCategory={isAllResourcesCategory}
                    hasAvailableResources={availableResources > 0}
                    hasAllResourcesLimitReached={hasAllResourcesLimitReached}
                    hasRateGroupLimitReached={hasRateGroupLimitReached}
                    serviceId={serviceId}
                    enterpriseId={enterpriseId}
                >
                    {isFormVisible ? (
                        <FormContent
                            hasDailyLimit={hasDailyLimit}
                            hasDirtyFields={hasDirtyFields}
                            onRemove={removeLimits}
                            submit={submit}
                        />
                    ) : (
                        <Button variant="primary" onClick={handleShowFormClick} type="submit">
                            {lt('CreateSellLimit')}
                        </Button>
                    )}
                </FormChecks>
                <OverlayedLoader show={isSubmitting} />
            </Stack>
        </StackItem>
    );
};
